// Templates are prompt starters the user can click to populate the
// InputBox. They are intentionally *prompts*, not hard-coded actions —
// once filled, the LLM decides which tools to call. This keeps the
// product flexible: a template like "Clean this data" works across any
// dataset because the LLM interprets it in context.
//
// When adding templates, favor verbs that describe *what the user wants*,
// not *how the system does it*. The LLM handles the how.

export interface Template {
  id: string;
  title: string;
  description: string;
  prompt: string;
  category: 'analyze' | 'clean' | 'create' | 'format' | 'explain';
  icon: string; // lucide icon name — mapped client-side
}

export const TEMPLATES: Template[] = [
  {
    id: 'clean-data',
    title: 'Clean this data',
    description: 'Remove duplicates, fix whitespace, standardize casing.',
    prompt:
      'Clean the data on the active sheet: remove duplicate rows, trim whitespace, fix inconsistent casing, and flag any cells that look malformed. Propose the changes so I can review before applying.',
    category: 'clean',
    icon: 'sparkles',
  },
  {
    id: 'find-anomalies',
    title: 'Find anomalies',
    description: 'Outliers, errors, values that don\'t match the pattern.',
    prompt:
      'Scan the active sheet for anomalies: numeric outliers (use IQR or z-score on columns that look numeric), Excel errors (#REF!, #DIV/0!), and values that break the column\'s pattern. List what you found with cell references.',
    category: 'analyze',
    icon: 'alert-triangle',
  },
  {
    id: 'explain-formula',
    title: 'Explain this formula',
    description: 'Decode what the formula in the active cell does.',
    prompt:
      'Look at the formula in my active cell. Explain step by step what it does, name the Excel functions it uses, and tell me if there\'s a simpler or more modern way to write it.',
    category: 'explain',
    icon: 'help-circle',
  },
  {
    id: 'build-kpis',
    title: 'Build a KPI dashboard',
    description: 'Pick metrics that fit the data and place them on a new sheet.',
    prompt:
      'Build a small KPI dashboard from the data on the active sheet. Create a new sheet named "Dashboard", add 3–5 KPIs that match the data (totals, averages, growth rates), and visualize the most important one as a chart. Use formulas that reference the source so the KPIs update automatically.',
    category: 'create',
    icon: 'layout-dashboard',
  },
  {
    id: 'add-pivot',
    title: 'Create a pivot table',
    description: 'Auto-choose row, column, and value fields from the data.',
    prompt:
      'Create a pivot table from the data on the active sheet. Pick row, column, and value fields that best reveal how the data breaks down. Place the pivot on a new sheet. Explain why you chose those fields.',
    category: 'create',
    icon: 'table',
  },
];
